import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

const EMPTY = {
  name: "", price: "", category: "tees", garment: "tee", angel: "111", is_new: false,
  colors: "Onyx", sizes: "S, M, L, XL", description: "", fabric: "", fit: "", details: "", shipping: "", meaning: "", image_url: "",
  gallery: "", inventory: "", color_images: "", drop_at: "",
};

const toForm = (p) => ({
  ...EMPTY, ...p, price: String(p.price), colors: p.colors.join(", "), sizes: p.sizes.join(", "), details: (p.details || []).join(", "),
  gallery: (p.gallery || []).join(", "),
  inventory: Object.entries(p.inventory || {}).map(([k, v]) => `${k}:${v}`).join(", "),
  color_images: Object.entries(p.color_images || {}).map(([k, v]) => `${k}:${v}`).join(", "),
  drop_at: (p.drop_at || "").slice(0, 16),
});
const toPayload = (f) => ({
  name: f.name, price: parseFloat(f.price) || 0, category: f.category, garment: f.garment, angel: f.angel, is_new: f.is_new,
  colors: f.colors.split(",").map((s) => s.trim()).filter(Boolean),
  sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
  description: f.description, fabric: f.fabric, fit: f.fit,
  details: f.details.split(",").map((s) => s.trim()).filter(Boolean),
  shipping: f.shipping || "Ships worldwide in 3-5 business days. Free shipping on orders over $200.",
  meaning: f.meaning, image_url: f.image_url, drop_at: f.drop_at || "",
  gallery: f.gallery.split(",").map((s) => s.trim()).filter(Boolean),
  inventory: Object.fromEntries(
    f.inventory.split(",").map((s) => s.trim()).filter(Boolean).map((pair) => {
      const [k, v] = pair.split(":");
      return [(k || "").trim(), parseInt(v, 10) || 0];
    }).filter(([k]) => k)
  ),
  color_images: Object.fromEntries(
    f.color_images.split(",").map((s) => s.trim()).filter(Boolean).map((pair) => {
      const idx = pair.indexOf(":");
      return [pair.slice(0, idx).trim(), pair.slice(idx + 1).trim()];
    }).filter(([k, v]) => k && v)
  ),
});

function Field({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] tracking-[0.3em] text-fade">{label}</span>
      <input
        data-testid={testId}
        {...props}
        className="mt-1.5 w-full border border-white/20 bg-transparent px-3 py-2.5 font-mono text-sm text-bone placeholder:text-bone/25 focus:border-bone focus:outline-none"
      />
    </label>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("products");
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (user === null)
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="font-mono text-xs tracking-[0.35em] text-fade">ALIGNING…</p>
      </div>
    );

  if (!user || user.role !== "admin")
    return (
      <div data-testid="admin-denied" className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="font-display text-3xl font-bold uppercase text-bone">Restricted</p>
        <p className="font-mono text-[10px] tracking-[0.3em] text-fade">ADMIN ACCESS ONLY</p>
        <Link to="/account" data-testid="admin-login-link" className="mt-4 border border-bone/40 px-6 py-3 font-mono text-xs tracking-[0.25em] text-bone hover:bg-bone hover:text-ink">
          GO TO LOGIN
        </Link>
      </div>
    );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = toPayload(form);
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/products/${id}`);
    toast.success("Product removed");
    load();
  };

  return (
    <div data-testid="admin-panel" className="mx-auto max-w-6xl px-5 pb-24 pt-32 sm:px-8">
      <p className="font-mono text-[10px] tracking-[0.35em] text-fade">ALIGNDEPT INTERNAL</p>
      <h1 className="mt-3 font-display text-4xl font-extrabold uppercase tracking-tight text-bone">Admin</h1>

      <div className="mt-8 flex gap-2">
        {["products", "orders"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.3em] transition-colors ${tab === t ? "border-bone bg-bone text-ink" : "border-white/15 text-bone/60 hover:text-bone"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="mt-10 grid gap-10 lg:grid-cols-12">
          <form onSubmit={save} className="space-y-4 lg:col-span-5" data-testid="admin-product-form">
            <p className="font-mono text-[10px] tracking-[0.3em] text-bone">{editingId ? `EDITING — ${editingId}` : "NEW PRODUCT"}</p>
            <Field label="NAME" testId="admin-input-name" value={form.name} onChange={set("name")} required />
            <div className="grid grid-cols-2 gap-4">
              <Field label="PRICE (USD)" testId="admin-input-price" type="number" step="0.01" value={form.price} onChange={set("price")} required />
              <label className="block">
                <span className="font-mono text-[9px] tracking-[0.3em] text-fade">ANGEL NUMBER</span>
                <select data-testid="admin-input-angel" value={form.angel} onChange={set("angel")} className="mt-1.5 w-full border border-white/20 bg-ink px-3 py-2.5 font-mono text-sm text-bone focus:border-bone focus:outline-none">
                  {["111", "222", "333", "444", "555"].map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="CATEGORY" testId="admin-input-category" value={form.category} onChange={set("category")} />
              <label className="block">
                <span className="font-mono text-[9px] tracking-[0.3em] text-fade">GARMENT TYPE</span>
                <select data-testid="admin-input-garment" value={form.garment} onChange={set("garment")} className="mt-1.5 w-full border border-white/20 bg-ink px-3 py-2.5 font-mono text-sm text-bone focus:border-bone focus:outline-none">
                  {["hoodie", "tee", "crewneck", "jacket", "pants", "cap"].map((g) => (
                    <option key={g}>{g}</option>
                  ))}
                </select>
              </label>
            </div>
            <Field label="COLORS (comma separated)" testId="admin-input-colors" value={form.colors} onChange={set("colors")} />
            <Field label="SIZES (comma separated)" testId="admin-input-sizes" value={form.sizes} onChange={set("sizes")} />
            <Field label="DESCRIPTION" testId="admin-input-description" value={form.description} onChange={set("description")} />
            <Field label="FABRIC / MATERIAL" testId="admin-input-fabric" value={form.fabric} onChange={set("fabric")} />
            <Field label="FIT" testId="admin-input-fit" value={form.fit} onChange={set("fit")} />
            <Field label="DETAILS (comma separated)" testId="admin-input-details" value={form.details} onChange={set("details")} />
            <Field label="THE MEANING" testId="admin-input-meaning" value={form.meaning} onChange={set("meaning")} />
            <Field label="IMAGE URL (optional — real photo replaces placeholder)" testId="admin-input-image" value={form.image_url} onChange={set("image_url")} />
            <Field label="GALLERY URLS (comma separated detail shots)" testId="admin-input-gallery" value={form.gallery} onChange={set("gallery")} />
            <Field label="INVENTORY (size:qty, e.g. S:4, M:0 — blank = unlimited)" testId="admin-input-inventory" value={form.inventory} onChange={set("inventory")} />
            <Field label="COLOR IMAGES (Color:/img/file.png — one per line or comma)" testId="admin-input-color-images" value={form.color_images} onChange={set("color_images")} />
            <Field label="DROP DATE & TIME (optional — locks buying + shows countdown)" testId="admin-input-drop-at" type="datetime-local" value={form.drop_at} onChange={set("drop_at")} />
            <label className="flex items-center gap-3">
              <input data-testid="admin-input-is-new" type="checkbox" checked={form.is_new} onChange={set("is_new")} className="h-4 w-4 accent-white" />
              <span className="font-mono text-[10px] tracking-[0.3em] text-fade">NEW RELEASE (DROP 001)</span>
            </label>
            <div className="flex gap-3">
              <button data-testid="admin-save-button" type="submit" disabled={busy} className="flex-1 bg-bone py-3.5 font-display text-xs font-extrabold uppercase tracking-[0.25em] text-ink hover:opacity-90 disabled:opacity-50">
                {busy ? "…" : editingId ? "SAVE CHANGES" : "CREATE PRODUCT"}
              </button>
              {editingId && (
                <button
                  type="button"
                  data-testid="admin-cancel-edit"
                  onClick={() => {
                    setEditingId(null);
                    setForm(EMPTY);
                  }}
                  className="border border-white/20 px-5 font-mono text-[10px] tracking-[0.25em] text-bone/70 hover:text-bone"
                >
                  CANCEL
                </button>
              )}
            </div>
          </form>

          <div className="lg:col-span-7">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex items-center justify-between gap-4 border-b border-white/10 py-4">
                <div>
                  <p className="font-display text-sm font-bold uppercase text-bone">{p.name}</p>
                  <p className="mt-1 font-mono text-[10px] tracking-widest text-fade">
                    {fmt(p.price)} — {p.angel} — {p.category.toUpperCase()} {p.is_new ? "— NEW" : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid={`admin-edit-${p.id}`}
                    onClick={() => {
                      setEditingId(p.id);
                      setForm(toForm(p));
                      window.scrollTo(0, 0);
                    }}
                    aria-label={`Edit ${p.name}`}
                    className="border border-white/20 p-2.5 text-bone/70 transition-colors hover:border-bone hover:text-bone"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p.id)} aria-label={`Delete ${p.name}`} className="border border-white/20 p-2.5 text-bone/70 transition-colors hover:border-red-400 hover:text-red-400">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-10" data-testid="admin-orders-list">
          {orders.length === 0 ? (
            <p className="border border-white/10 p-8 font-mono text-xs tracking-[0.25em] text-fade">NO ORDERS YET.</p>
          ) : (
            orders.map((o) => (
              <div key={o.id} data-testid={`admin-order-${o.id}`} className="mb-4 border border-white/10 p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display text-lg font-bold text-bone">{o.id}</p>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-fade">
                    {o.email || "GUEST"} — {new Date(o.created_at).toLocaleString()} — {o.status.toUpperCase()}
                  </p>
                </div>
                <div className="mt-4 space-y-1">
                  {o.items.map((i, idx) => (
                    <p key={idx} className="font-mono text-xs tracking-widest text-bone/70">
                      {i.qty}× {i.name} — {i.color} / {i.size}
                    </p>
                  ))}
                </div>
                <p className="mt-4 font-mono text-sm text-bone">TOTAL {fmt(o.total)}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
